import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";

/** Sidebar language picker: dark popover that opens upward from the footer. */
const LanguageSwitcher: React.FC = () => {
  const { code, languages, setLanguage, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.language")}
        className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-colors font-medium ${
          open ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
        }`}
      >
        <i className="fa-solid fa-globe w-5 text-center text-base"></i>
        <span className="flex-1 text-left truncate">{languages[code] ?? code}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-700/60 rounded px-1.5 py-0.5">
          {code}
        </span>
        <i
          className={`fa-solid fa-chevron-up text-xs text-gray-400 transition-transform duration-200 ${
            open ? "" : "rotate-180"
          }`}
        ></i>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("common.language")}
          className="absolute bottom-full left-0 right-0 mb-2 z-50 max-h-64 overflow-y-auto p-1.5 rounded-xl bg-gray-800 border border-gray-700 shadow-2xl shadow-black/40"
        >
          {Object.entries(languages).map(([value, label]) => {
            const selected = value === code;
            return (
              <li key={value} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    setLanguage(value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    selected
                      ? "bg-white text-gray-900 font-semibold"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                  }`}
                >
                  <span className="w-7 text-[10px] font-semibold uppercase tracking-wider opacity-60">
                    {value}
                  </span>
                  <span className="flex-1 text-left truncate">{label}</span>
                  {selected && <i className="fa-solid fa-check text-xs"></i>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
